/**
 * Step 4: Metadata (OPTIONAL)
 *
 * Manage dataset metadata key-value pairs (optional, max 50 entries)
 */

import React, { useState } from 'react';
import { Plus, Trash2, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { useDatasetMetadata } from '@/features/datasets/store/datasetCreationStore';
import { validateMetadata } from '@/features/datasets/utils/datasetValidation';

/**
 * Metadata Step Component
 */
export const DatasetMetadataStep: React.FC = () => {
  const { metadata, addMetadata, removeMetadata, updateMetadata } = useDatasetMetadata();
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  // Validate metadata
  const validationErrors = validateMetadata(metadata);
  const hasErrors = validationErrors.length > 0;
  const isAtLimit = metadata.length >= 50;

  /**
   * Handle add metadata entry
   */
  const handleAddMetadata = () => {
    const trimmedKey = newKey.trim();
    const trimmedValue = newValue.trim();

    if (!trimmedKey || !trimmedValue) return;

    // Check for duplicates
    const isDuplicate = metadata.some(
      (m) => m.key.toLowerCase() === trimmedKey.toLowerCase()
    );

    if (isDuplicate) {
      return;
    }

    // Check valid key
    if (!/^[a-zA-Z0-9_]+$/.test(trimmedKey)) {
      return;
    }

    // Check limit
    if (metadata.length >= 50) {
      return;
    }

    addMetadata(trimmedKey, trimmedValue);
    setNewKey('');
    setNewValue('');
  };

  /**
   * Handle remove metadata entry
   */
  const handleRemoveMetadata = (id: string) => {
    removeMetadata(id);
  };

  /**
   * Handle update metadata entry
   */
  const handleUpdateMetadata = (id: string, key: string, value: string) => {
    updateMetadata(id, key, value);
  };

  /**
   * Handle key press in inputs
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newKey.trim() && newValue.trim()) {
      e.preventDefault();
      handleAddMetadata();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Metadata</h2>
        <p className="text-muted-foreground text-sm">
          Add optional metadata key-value pairs to provide additional context for your dataset.
        </p>
      </div>

      {/* Validation Errors */}
      {hasErrors && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>{error.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Entry Count */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {metadata.length === 0 ? 'No metadata entries' : `${metadata.length} / 50 entries`}
        </span>
        {isAtLimit && (
          <span className="text-destructive font-medium">Maximum entries reached</span>
        )}
      </div>

      {/* Existing Metadata List */}
      {metadata.length > 0 && (
        <div className="space-y-2">
          <Label>Configured Metadata</Label>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {metadata.map((entry) => (
              <Card key={entry.id} className="p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Key</Label>
                      <Input
                        value={entry.key}
                        onChange={(e) =>
                          handleUpdateMetadata(entry.id, e.target.value, entry.value)
                        }
                        placeholder="key"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Value</Label>
                      <Input
                        value={entry.value}
                        onChange={(e) =>
                          handleUpdateMetadata(entry.id, entry.key, e.target.value)
                        }
                        placeholder="value"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveMetadata(entry.id)}
                    className="text-destructive hover:text-destructive mt-6"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Add New Metadata */}
      <div className="space-y-3">
        <Label>Add Metadata Entry</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Key (e.g., owner, department)"
              disabled={isAtLimit}
            />
          </div>
          <div>
            <Input
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Value"
              disabled={isAtLimit}
            />
          </div>
        </div>
        <Button
          onClick={handleAddMetadata}
          disabled={!newKey.trim() || !newValue.trim() || isAtLimit}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Metadata Entry
        </Button>
        <p className="text-xs text-muted-foreground">
          Keys must be alphanumeric with underscores only. Press Enter to add quickly.
        </p>
      </div>

      {/* Info Box */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Optional:</strong> Metadata helps categorize and document your datasets.
          Common examples include owner, department, data_source, update_frequency, or
          retention_policy. You can skip this step if no metadata is needed.
        </AlertDescription>
      </Alert>
    </div>
  );
};
